import React, { useEffect, useMemo, useState } from "react";
import useCourseSets from "../../hooks/useCourseSets";
import { getCurrentColumns, getParentNodeId } from "./modules/Utils";
import useCurriculumSets from "../../hooks/useCurriculumSets";
import { getFirstSetWithSelectedStandards } from "../../modules/utils";
import SelectStandardsModal from "../SelectStandardsModal";
import SelectSetModal from "../SelectSetModal";
import _ from "lodash";
import useSubjectSet from "../../hooks/useSubjectSet";
import { getElementTypeLabels } from "Teacher/routes/Courses/routes/Course/routes/UnitPlans/routes/UnitPlanDetails/routes/EditUnitPlan/routes/IBPlanner/modules/IBPlannerModule";
import { useI18n } from "Hooks";
import {
  NODE_SOURCE_OPTIONS,
  NODE_SOURCES,
} from "../../modules/stringConstants";
import { getIsLearningCoursesEnabledForUnit } from "UnitPlans/modules/UnitViewModeUtils";

const SubjectBasedGradingWrapper = props => {
  const {
    onClose,
    value,
    label,
    subjectIds,
    parentNodeId: _parentNodeId,
    learningCourseId,
    options,
    isOldSubjectBasedGrading,
    plannerElementType,
    updateTree,
    onSave,
    initialFilters,
    treeMapRef,
    resolvedValue,
  } = props;
  const { t } = useI18n(["common"]);
  const { parentNodes } = options;

  const parentNodeId = getParentNodeId({
    parentNodes,
  });

  const isLearningCourseEnabled = getIsLearningCoursesEnabledForUnit();

  const updatedLabel =
    getElementTypeLabels({ t, type: plannerElementType }) || label;
  const [standardSetId, setStandardSetId] = useState(undefined);

  const [selectedNodesSource, setSelectedNodesSource] = useState(
    isLearningCourseEnabled ? NODE_SOURCE_OPTIONS(t)[0] : undefined
  );

  const [selectedStandards, setSelectedStandards] = useState(
    new Set(value || [])
  );

  useEffect(() => {
    setSelectedStandards(new Set(value) || []);
  }, [value]);

  const showCourseStandards =
    selectedNodesSource?.value === NODE_SOURCES.COURSE;

  // This is called when there is only one parent node or parent node is passed from parent
  // This fetches the subject standard set details
  // It will be called for both toddle 2.0 and toddle 1.0
  const {
    idsToPropertiesMap: subjectMap,
    showLoader: subjectLoader,
    standardEdges: subjectEdges,
  } = useSubjectSet({ parentNodeId, showCourseStandards, learningCourseId });

  // This is called when there are multiple parent nodes and we need to fetch course sets
  // Will only be called for toddle 2.0 as they are shown when showCourseStandards switcher is toggled
  const {
    idsToPropertiesMap: courseMap,
    showLoader: courseLoader,
    standardEdges: courseEdges,
  } = useCourseSets({
    learningCourseId,
    subjectIds,
    skip: !!parentNodeId,
  });

  // This is called when there are multiple parent nodes and we need to fetch curriculum sets
  // It will be called for both toddle 2.0 and toddle 1.0
  const {
    idsToPropertiesMap: curriculumMap,
    showLoader: curriculumLoader,
    standardEdges: curriculumEdges,
  } = useCurriculumSets({ subjectIds, skip: !!parentNodeId });

  const showLoader = parentNodeId
    ? subjectLoader
    : showCourseStandards
    ? courseLoader
    : curriculumLoader;
  const idsToPropertiesMap = parentNodeId
    ? subjectMap
    : showCourseStandards
    ? courseMap
    : curriculumMap;
  const standardEdges = parentNodeId
    ? subjectEdges
    : showCourseStandards
    ? courseEdges
    : curriculumEdges;

  const standardSetCorrespondingToParentNodeId = useMemo(() => {
    const parentIdToOpen = parentNodeId || _parentNodeId;
    if (!parentIdToOpen) return null;

    return _.find(
      standardEdges,
      edge => edge?.standardSet?.subject?.id === parentIdToOpen
    )?.standardSet?.id;
  }, [parentNodeId, _parentNodeId, standardEdges]);

  const firstSetWithSelectedStandards = useMemo(
    () => getFirstSetWithSelectedStandards({ standardEdges, value }),
    [standardEdges, value]
  );

  useEffect(() => {
    if (standardSetCorrespondingToParentNodeId) {
      setStandardSetId(prev =>
        prev || prev === null ? prev : standardSetCorrespondingToParentNodeId
      );
      return;
    }

    if (firstSetWithSelectedStandards) {
      setStandardSetId(prev =>
        prev || prev === null ? prev : firstSetWithSelectedStandards
      );
    }
  }, [
    firstSetWithSelectedStandards,
    standardEdges,
    standardSetCorrespondingToParentNodeId,
  ]);

  useEffect(() => {
    setStandardSetId(prev => {
      if (!prev) return prev;
      if (idsToPropertiesMap[prev]) return prev;

      if (_.isEmpty(standardEdges)) return undefined;
      return firstSetWithSelectedStandards;
    });
  }, [firstSetWithSelectedStandards, idsToPropertiesMap, standardEdges]);

  const columns = useMemo(() => {
    return getCurrentColumns({
      isOldSubjectBasedGrading,
      plannerElementType,
    });
  }, [isOldSubjectBasedGrading, plannerElementType]);

  return standardSetId ? (
    <SelectStandardsModal
      onClose={onClose}
      setStandardSetId={setStandardSetId}
      standardSetId={standardSetId}
      label={updatedLabel}
      standardEdges={standardEdges}
      idsToPropertiesMap={idsToPropertiesMap}
      selectedNodesSource={selectedNodesSource}
      setSelectedNodesSource={setSelectedNodesSource}
      learningCourseId={learningCourseId}
      hideBackAndBreadcrumbs={!!parentNodeId}
      columns={columns}
      selectedStandards={selectedStandards}
      setSelectedStandards={setSelectedStandards}
      updateTree={updateTree}
      onSave={onSave}
      plannerElementType={plannerElementType}
      initialFilters={initialFilters}
      treeMapRef={treeMapRef}
      resolvedMinimalTree={resolvedValue}
    />
  ) : (
    <SelectSetModal
      onClose={onClose}
      setStandardSetId={setStandardSetId}
      label={updatedLabel}
      standardEdges={standardEdges}
      selectedStandards={selectedStandards}
      selectedNodesSource={selectedNodesSource}
      setSelectedNodesSource={setSelectedNodesSource}
      showLoader={showLoader}
    />
  );
};

export default SubjectBasedGradingWrapper;
